import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { zodResolver } from '@hookform/resolvers/zod';
import { editCategorySchema, EditCategoryFormValues } from '../../../schemas/category/categorySchemas';
import { TipoPontuacao, StatusCategoriaTorneio } from '../../../types/categoriaTorneio';
import { getTiebreakOptions, parseTiebreakValue, getValidTiebreakValues } from '../../../utils/tiebreakOptions';
import { FilaEsperaToggle } from './FilaEsperaToggle';
import type { CategoriaLiga } from '../../../types/categoriaLiga';

/** null → undefined: os campos-template da CategoriaLiga são nullable; o form usa undefined. */
const nz = <T,>(v: T | null | undefined): T | undefined => (v == null ? undefined : v);

export interface EditCategoryFormProps {
  initialValues: EditCategoryFormValues & {
    quantidadeFases: number;
    torneioNome: string;
  };
  onSubmit: (values: EditCategoryFormValues) => Promise<void> | void;
  onCancel: () => void;
  hasMatches?: boolean;
  modeloDisputa?: string | null;
  isCreate?: boolean;
  validarElegibilidade?: boolean;
  /**
   * Templates de CategoriaLiga disponíveis para pré-preenchimento (AC-2). Buscados
   * pela page que hospeda este formulário (nunca pelo componente — anti-pattern
   * "componentes que chamam services diretamente") e passados por prop. O seletor só
   * é renderizado quando há ao menos 1 template (torneio pertence a uma liga com
   * catálogo).
   */
  categoriasLigaTemplate?: CategoriaLiga[];
}

const EditCategoryForm: React.FC<EditCategoryFormProps> = ({ initialValues, onSubmit, onCancel, hasMatches = false, modeloDisputa: propModeloDisputa, isCreate = false, validarElegibilidade = false, categoriasLigaTemplate }) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<EditCategoryFormValues>({
    resolver: zodResolver(editCategorySchema),
    defaultValues: initialValues,
  });
  // Sanitiza o estado inicial: uma categoria salva no passado pode ter uma
  // combinação inconsistente (ex.: 6 games por set com "tiebreak no 1 a 1"),
  // que faria placares legítimos como 6x4 serem rejeitados na validação e
  // bloquearia o salvamento pela checagem cruzada do schema. Ao carregar,
  // limpamos um tiebreak que não seja opção válida para o games por set.
  useEffect(() => {
    const tiebreakValido =
      initialValues.tiebreaks == null ||
      initialValues.gamesPorSets == null ||
      getValidTiebreakValues(initialValues.gamesPorSets).includes(initialValues.tiebreaks);
    reset(tiebreakValido ? initialValues : { ...initialValues, tiebreaks: undefined });
  }, [initialValues, reset]);

  // Mantém o tiebreak coerente quando o usuário troca o games por set no
  // formulário: limpa um tiebreak que deixou de ser uma opção válida (o select
  // de games por set fica desabilitado quando há partidas, então este efeito
  // só dispara em categorias ainda editáveis).
  const gamesPorSetsAtual = watch('gamesPorSets');
  const tiebreaksAtual = watch('tiebreaks');
  useEffect(() => {
    if (
      tiebreaksAtual != null &&
      gamesPorSetsAtual != null &&
      !getValidTiebreakValues(gamesPorSetsAtual).includes(tiebreaksAtual)
    ) {
      setValue('tiebreaks', undefined, { shouldValidate: true, shouldDirty: true });
    }
  }, [gamesPorSetsAtual, tiebreaksAtual, setValue]);

  const modeloDisputa = watch('modeloDisputa') || propModeloDisputa;
  const numeroAtletas = watch('numeroAtletas');
  const isDupla = Number(numeroAtletas) === 2;
  // Rodízio (Rei/Rainha, Super 8) é sempre individual (solo): força
  // numeroAtletas=1 e desabilita o campo "Tipo" para edição.
  const isRodizio = modeloDisputa === 'REI_RAINHA' || modeloDisputa === 'SUPER_8';
  useEffect(() => {
    if (isRodizio && Number(numeroAtletas) !== 1) {
      setValue('numeroAtletas', 1, { shouldValidate: true, shouldDirty: true });
    }
  }, [isRodizio, numeroAtletas, setValue]);

  // Pré-preenchimento one-shot a partir de uma CategoriaLiga escolhida como template
  // (AC-2). Copia só os campos-template — datas, status, vagas, aplicarDescontoTorneio
  // e configFilaEspera permanecem por-torneio. Sem seleção, categoriaLigaId fica
  // indefinido e nada é sobrescrito (AC-5).
  const handleTemplateChange = (id: string) => {
    const opts = { shouldValidate: true, shouldDirty: true } as const;
    if (!id) {
      setValue('categoriaLigaId', undefined, opts);
      return;
    }
    const template = (categoriasLigaTemplate ?? []).find((t) => t.id === id);
    if (!template) return;
    setValue('categoriaLigaId', template.id, opts);
    if (template.nome != null) setValue('nome', template.nome, opts);
    setValue('descricao', nz(template.descricao), opts);
    if (template.genero != null) setValue('genero', template.genero, opts);
    setValue('idadeInicial', nz(template.idadeInicial), opts);
    setValue('idadeFinal', nz(template.idadeFinal), opts);
    setValue('idadeCombinada', nz(template.idadeCombinada), opts);
    setValue('modeloDisputa', nz(template.modeloDisputa), opts);
    setValue('tipoPontuacao', (nz(template.tipoPontuacao) as unknown) as TipoPontuacao | undefined, opts);
    setValue('numeroSets', nz(template.numeroSets), opts);
    setValue('gamesPorSets', nz(template.gamesPorSets), opts);
    setValue('tiebreaks', nz(template.tiebreaks), opts);
    setValue('quantidadeGrupos', nz(template.quantidadeGrupos), opts);
    setValue('classificadosPorGrupo', nz(template.classificadosPorGrupo), opts);
    setValue('tamanhoGrupo', nz(template.tamanhoGrupo), opts);
    if (template.precoInscricao != null) setValue('precoInscricao', template.precoInscricao, opts);
    if (template.numeroAtletas != null) setValue('numeroAtletas', template.numeroAtletas, opts);
  };

  return (
    <>
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-3">
        <FormControlLabel
          control={
            <Controller
              name="aplicarDescontoTorneio"
              control={control}
              render={({ field }) => (
                <Switch {...field} checked={!!field.value} />
              )}
            />
          }
          label="Aplicar descontos por categoria de torneio"
        />
      </div>
      {categoriasLigaTemplate && categoriasLigaTemplate.length > 0 && (
        <div className="row mb-3">
          <div className="col-md-6">
            <label htmlFor="categoria-liga-template" className="form-label">Categoria da Liga (template)</label>
            <select
              id="categoria-liga-template"
              className="form-select"
              aria-label="Categoria da Liga (template)"
              data-testid="categoria-liga-template-select"
              value={watch('categoriaLigaId') ?? ''}
              onChange={(e) => handleTemplateChange(e.target.value)}
            >
              <option value="">Nenhuma (categoria avulsa)</option>
              {categoriasLigaTemplate.map((t) => (
                <option key={t.id} value={t.id}>{t.nome ?? '(sem nome)'}</option>
              ))}
            </select>
            <div className="form-text">Pré-preenche os campos a partir de uma categoria da liga. Datas e status permanecem por torneio.</div>
          </div>
        </div>
      )}
      <h5 className="mb-3">Dados básicos</h5>
      <div className="row mb-3">
        <div className="form-text mb-2 col-4">
          <label htmlFor="nome" className="form-label">Nome</label>
          <input id="nome" className="form-control" aria-label="Nome" data-testid="category-name" {...register('nome')} />
          {errors.nome && <div className="text-danger small">{errors.nome.message}</div>}
        </div>
      </div>
      <div className="row">
        <div className="col-md-4 mb-3">
          <label htmlFor="numero-atletas" className="form-label">Tipo</label>
          <select id="numero-atletas" className="form-select" aria-label="Tipo" disabled={isRodizio} {...register('numeroAtletas', {
            setValueAs: (value) => (value === '' ? undefined : Number(value)),
          })}>
            <option value="1">Simples</option>
            <option value="2">Dupla</option>
          </select>
          {errors.numeroAtletas && <div className="text-danger small">{errors.numeroAtletas.message}</div>}
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="precoInscricao" className="form-label">Preço inscrição</label>
          <input
            type="number"
            step="0.01"
            id="precoInscricao"
            className="form-control"
            aria-label="Preço inscrição"
            {...register('precoInscricao', { valueAsNumber: true })}
          />
          {errors.precoInscricao && <div className="text-danger small">{errors.precoInscricao.message}</div>}
        </div>
        {!isCreate && (
          <div className="col-md-4 mb-3">
            <label htmlFor="status" className="form-label">Status</label>
            <select id="status" className="form-select" aria-label="Status" {...register('status')}>
              <option value="">Selecione...</option>
              {Object.values(StatusCategoriaTorneio).map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            {errors.status && <div className="text-danger small">{errors.status.message}</div>}
          </div>
        )}
      </div>
      <div className="row">
        <div className="col-md-4 mb-3">
          <label htmlFor="vagas" className="form-label">Vagas</label>
          <input type="number" id="vagas" className="form-control" {...register('vagas', { valueAsNumber: true })} />
          {errors.vagas && <div className="text-danger small">{errors.vagas.message}</div>}
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="modeloDisputa" className="form-label">Modelo de Disputa</label>
          <select id="modeloDisputa" className="form-select" {...register('modeloDisputa')} disabled={hasMatches}>
            <option value="ELIMINACAO_SIMPLES">Eliminação Simples</option>
            <option value="ELIMINACAO_DUPLA">Eliminação Dupla</option>
            <option value="GRUPOS_ELIMINACAO">Grupos + Eliminação</option>
            <option value="GRUPO_UNICO">Grupo Único</option>
            <option value="REI_RAINHA">Rei/Rainha da Quadra</option>
            <option value="SUPER_8">Super 8</option>
          </select>
          {errors.modeloDisputa && <div className="text-danger small">{errors.modeloDisputa.message}</div>}
        </div>
      </div>
      <h5 className="mt-4 mb-3">Configurações de Partidas</h5>
      <div className="row">
        {modeloDisputa === 'GRUPOS_ELIMINACAO' && (
          <>
            <div className="col-md-4 mb-3">
              <label htmlFor="quantidadeGrupos" className="form-label">Quantidade de Grupos</label>
              <input
                type="number"
                id="quantidadeGrupos"
                className="form-control"
                min="1"
                {...register('quantidadeGrupos', { valueAsNumber: true })}
                disabled={hasMatches}
              />
              {errors.quantidadeGrupos && <div className="text-danger small">{errors.quantidadeGrupos.message}</div>}
            </div>
            <div className="col-md-4 mb-3">
              <label htmlFor="classificadosPorGrupo" className="form-label">Classificados por Grupo</label>
              <input
                type="number"
                id="classificadosPorGrupo"
                className="form-control"
                min="1"
                {...register('classificadosPorGrupo', { valueAsNumber: true })}
                disabled={hasMatches}
              />
              {errors.classificadosPorGrupo && <div className="text-danger small">{errors.classificadosPorGrupo.message}</div>}
            </div>
          </>
        )}
        {modeloDisputa === 'REI_RAINHA' && (
          <div className="col-md-4 mb-3">
            <label htmlFor="tamanhoGrupo" className="form-label">Tamanho de Grupo</label>
            <select
              id="tamanhoGrupo"
              className="form-select"
              {...register('tamanhoGrupo', { valueAsNumber: true })}
              disabled={hasMatches}
            >
              <option value="4">4</option>
              <option value="8">8</option>
            </select>
            {errors.tamanhoGrupo && <div className="text-danger small">{errors.tamanhoGrupo.message}</div>}
          </div>
        )}
        <div className="col-md-4 mb-3">
          <label htmlFor="tipoPontuacao" className="form-label">Tipo de Pontuação</label>
          <select id="tipoPontuacao" className="form-select" {...register('tipoPontuacao')} disabled={hasMatches}>
            <option value={TipoPontuacao.SETSGAMES}>Sets e Games</option>
            {/* <option value={TipoPontuacao.SETSPONTOS}>Sets e Pontos</option> */}
          </select>
          {errors.tipoPontuacao && <div className="text-danger small">{errors.tipoPontuacao.message}</div>}
        </div>
      </div>
      <div className="row">
        <div className="col-md-4 mb-3">
          <label htmlFor="numeroSets" className="form-label">Número de Sets</label>
          <select
            id="numeroSets"
            className="form-select"
            {...register('numeroSets', {
              setValueAs: (value) => (value === '' ? undefined : Number(value)),
            })}
            disabled={hasMatches}
          >
            <option value="1">1 Set</option>
            <option value="2">2 Sets + Super Tiebreak</option>
            <option value="3">3 Sets</option>
            <option value="4">4 Sets + Super Tiebreak</option>
            <option value="5">5 Sets</option>
          </select>
          {errors.numeroSets && <div className="text-danger small">{errors.numeroSets.message}</div>}
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="gamesPorSets" className="form-label">Games por Set</label>
          <select
            id="gamesPorSets"
            className="form-select"
            {...register('gamesPorSets', {
              setValueAs: (value) => (value === '' ? undefined : Number(value)),
            })}
            disabled={hasMatches}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((num) => (
              <option key={num} value={num.toString()}>
                {num} {num === 1 ? 'Game' : 'Games'}
              </option>
            ))}
          </select>
          {errors.gamesPorSets && <div className="text-danger small">{errors.gamesPorSets.message}</div>}
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="tiebreaks" className="form-label">Tiebreak</label>
          <select
            id="tiebreaks"
            className="form-select"
            {...register('tiebreaks', {
              setValueAs: (value) => {
                // Converter 'null' string para null, string vazia para undefined, ou número
                if (value === '' || value === 'null') {
                  return undefined; // undefined será tratado como null pelo backend
                }
                const num = Number(value);
                return isNaN(num) ? undefined : num;
              },
            })}
            disabled={hasMatches || !watch('gamesPorSets')}
          >
            {getTiebreakOptions(watch('gamesPorSets')).map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.tiebreaks && <div className="text-danger small">{errors.tiebreaks.message}</div>}
        </div>
      </div>
      
      <h5 className="mt-4 mb-3">Configurações de Data</h5>
      <div className="row">
        <div className="col-md-4 mb-3">
          <label htmlFor="dataInicio" className="form-label">Data Início</label>
          <input type="date" id="dataInicio" className="form-control" {...register('dataInicio')} />
          {errors.dataInicio && <div className="text-danger small">{errors.dataInicio.message}</div>}
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="dataFim" className="form-label">Data Fim</label>
          <input type="date" id="dataFim" className="form-control" {...register('dataFim')} />
          {errors.dataFim && <div className="text-danger small">{errors.dataFim.message}</div>}
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="dataLimPag" className="form-label">Data Limite Pagamento</label>
          <input type="date" id="dataLimPag" className="form-control" {...register('dataLimPag')} />
          {errors.dataLimPag && <div className="text-danger small">{errors.dataLimPag.message}</div>}
        </div>
      </div>
      {validarElegibilidade && (
        <>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label htmlFor="genero" className="form-label">Gênero</label>
              <select id="genero" className="form-select" aria-label="Gênero" {...register('genero')}>
                <option value="MASCULINO">Masculino</option>
                <option value="FEMININO">Feminino</option>
                <option value="MISTO">Misto</option>
              </select>
              {errors.genero && <div className="text-danger small">{errors.genero.message}</div>}
            </div>
          </div>
          <h5 className="mt-4 mb-3">Configurações de Idade</h5>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label htmlFor="idadeInicial" className="form-label">Idade Inicial</label>
              <input type="number" id="idadeInicial" className="form-control" {...register('idadeInicial', { valueAsNumber: true })} />
              {errors.idadeInicial && <div className="text-danger small">{errors.idadeInicial.message}</div>}
            </div>
            <div className="col-md-4 mb-3">
              <label htmlFor="idadeFinal" className="form-label">Idade Final</label>
              <input type="number" id="idadeFinal" className="form-control" {...register('idadeFinal', { valueAsNumber: true })} />
              {errors.idadeFinal && <div className="text-danger small">{errors.idadeFinal.message}</div>}
            </div>
            {isDupla && (
              <div className="col-md-4 mb-3">
                <label htmlFor="idadeCombinada" className="form-label">Idade Combinada</label>
                <input type="number" id="idadeCombinada" className="form-control" {...register('idadeCombinada', { valueAsNumber: true })} />
                {errors.idadeCombinada && <div className="text-danger small">{errors.idadeCombinada.message}</div>}
              </div>
            )}
          </div>
        </>
      )}

      <h5 className="mt-4 mb-3">Fila de espera</h5>
      <div className="mb-3">
        <Controller
          name="configFilaEspera"
          control={control}
          render={({ field }) => (
            <FilaEsperaToggle
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="d-flex gap-2 justify-content-end mt-4">
        <button type="button" className="btn btn-secondary" onClick={() => { reset(); onCancel(); }}>Cancelar</button>
        <button type="submit" className="btn btn-primary" disabled={isSubmitting} data-testid="category-create-submit">Salvar alterações</button>
      </div>
    </form>
    </>
  );
};

export default EditCategoryForm;
